import React, { useEffect, useMemo, useState } from 'react';
import { ScrollText, Save, RotateCcw, Plus, Trash2, Clock, AlertTriangle, User, Tag } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Badge } from '../../../../components/ui/Badge';
import { Input } from '../../../../components/ui/Input';
import { loadGovernanceConfig, saveGovernanceConfig, type GovernanceConfigOverrides, type GraceLedgerEntry } from '../../../../governance';
/**
 * GraceLedgerSection — FG4 §Fase C UI (S9 Deelblok B).
 *
 * Surfaces and manages the grace ledger — NAMED, OWNED, EXPIRING
 * exceptions that temporarily degrade severity for a scope.
 *
 * Anti-bandaid principle #3 (made physical): every permissive shortcut
 * lives here with an owner, a reason, and an expiry date. No more
 * implicit `return OK` debt.
 *
 * Owns its own load/save lifecycle (additive, mirrors EnforcementSection).
 */
const DAY_MS = 24 * 60 * 60 * 1000;
function formatCountdown(untilIso: string, now: number): {
  text: string;
  cls: string;
  expired: boolean;
} {
  const until = Date.parse(untilIso);
  if (!Number.isFinite(until)) {
    return {
      text: 'Ongeldige datum',
      cls: 'text-red-600',
      expired: true
    };
  }
  const diff = until - now;
  if (diff < 0) {
    const daysAgo = Math.floor(-diff / DAY_MS);
    return {
      text: `Verlopen (${daysAgo}d geleden)`,
      cls: 'text-red-600 font-semibold',
      expired: true
    };
  }
  const days = Math.floor(diff / DAY_MS);
  if (days <= 3) {
    return {
      text: `Nog ${days} dag${days === 1 ? '' : 'en'}`,
      cls: 'text-amber-700 font-semibold',
      expired: false
    };
  }
  if (days <= 14) {
    return {
      text: `Nog ${days} dagen`,
      cls: 'text-amber-600',
      expired: false
    };
  }
  return {
    text: `Nog ${days} dagen`,
    cls: 'text-emerald-700',
    expired: false
  };
}
function defaultUntilIso(): string {
  const d = new Date(Date.now() + 30 * DAY_MS);
  return d.toISOString().slice(0, 10);
}
interface DraftEntry {
  module: string;
  family: string;
  questionId: string;
  until: string;
  reason: string;
  owner: string;
  degradeToSeverity: string; // string so the input can be empty
}
const EMPTY_DRAFT: DraftEntry = {
  module: '',
  family: '',
  questionId: '',
  until: defaultUntilIso(),
  reason: '',
  owner: '',
  degradeToSeverity: '50'
};
function scopeLabel(scope: GraceLedgerEntry['scope']): string {
  const parts: string[] = [];
  if (scope.module) parts.push(`module:${scope.module}`);
  if (scope.family) parts.push(`family:${scope.family}`);
  if (scope.questionId) parts.push(`qid:${scope.questionId}`);
  return parts.join(' · ') || '(globaal)';
}
function scopeSpecificity(scope: GraceLedgerEntry['scope']): {
  level: 'question' | 'family' | 'module' | 'global';
  cls: string;
} {
  if (scope.questionId) return {
    level: 'question',
    cls: 'bg-violet-100 text-violet-700 border-violet-200'
  };
  if (scope.family) return {
    level: 'family',
    cls: 'bg-blue-100 text-blue-700 border-blue-200'
  };
  if (scope.module) return {
    level: 'module',
    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };
  return {
    level: 'global',
    cls: 'bg-slate-100 text-slate-700 border-slate-200'
  };
}
export function GraceLedgerSection() {
  const [entries, setEntries] = useState<GraceLedgerEntry[]>([]);
  const [originalEntries, setOriginalEntries] = useState<GraceLedgerEntry[]>([]);
  const [draft, setDraft] = useState<DraftEntry>(EMPTY_DRAFT);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [now] = useState(() => Date.now());
  useEffect(() => {
    let cancelled = false;
    loadGovernanceConfig().then(cfg => {
      if (cancelled) return;
      const ledger = cfg.graceLedger ?? [];
      setEntries(ledger);
      setOriginalEntries(ledger);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const dirty = useMemo(() => JSON.stringify(entries) !== JSON.stringify(originalEntries), [entries, originalEntries]);
  const handleSave = async () => {
    const cfg = await loadGovernanceConfig();
    const nextCfg: GovernanceConfigOverrides = {
      ...cfg,
      graceLedger: entries
    };
    const ok = await saveGovernanceConfig(nextCfg);
    if (ok) {
      setOriginalEntries(entries);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };
  const handleReset = () => setEntries(originalEntries);
  const handleRemove = (index: number) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  };
  const handleAdd = () => {
    setFormError(null);
    const scope: GraceLedgerEntry['scope'] = {};
    if (draft.module.trim()) scope.module = draft.module.trim().toUpperCase();
    if (draft.family.trim()) scope.family = draft.family.trim();
    if (draft.questionId.trim()) scope.questionId = draft.questionId.trim();
    if (!scope.module && !scope.family && !scope.questionId) {
      setFormError('Scope is verplicht: vul minstens een module, family of questionId in.');
      return;
    }
    if (!draft.reason.trim()) {
      setFormError('Reden is verplicht (anti-bandaid principe #3: zichtbare schuld).');
      return;
    }
    if (!draft.owner.trim()) {
      setFormError('Eigenaar is verplicht — elke uitzondering heeft een persoon die hem sluit.');
      return;
    }
    if (!draft.until || Number.isNaN(Date.parse(draft.until))) {
      setFormError('Vervaldatum is verplicht en moet een geldige datum zijn.');
      return;
    }
    const entry: GraceLedgerEntry = {
      scope,
      until: new Date(draft.until).toISOString(),
      reason: draft.reason.trim(),
      owner: draft.owner.trim()
    };
    const sev = Number(draft.degradeToSeverity);
    if (Number.isFinite(sev) && sev > 0 && sev <= 100) {
      entry.degradeToSeverity = sev;
    }
    setEntries(prev => [...prev, entry]);
    setDraft(EMPTY_DRAFT);
    setShowForm(false);
  };
  const sorted = useMemo(() => {
    return [...entries].map((e, idx) => ({
      entry: e,
      idx,
      until: Date.parse(e.until)
    })).sort((a, b) => a.until - b.until);
  }, [entries]);
  return <div data-id="element-4573" data-mp="3gabtj1d6qarh">
      <div className="flex items-start justify-between gap-3 mb-1" data-id="element-4574" data-mp="3ga3gn9h2b5ga">
        <div data-id="element-4575" data-mp="2jlnlmxv6o3vs">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2" data-id="element-4576" data-mp="2jm5hhtdxog36">
            <ScrollText className="w-4 h-4 text-violet-500" data-id="element-4577" data-mp="2nm7gtvw74xfa" />
            Grace Ledger
            <Badge variant="info" size="sm" data-id="element-4578" data-mp="2sm2a263g1z3i">
              FG4 · Fase C
            </Badge>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4579" data-mp="2xcsdvew81tw3">
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={!dirty} data-id="element-4580" data-mp="1zx4u49mpgedx">
            <RotateCcw className="w-3.5 h-3.5 mr-1" data-id="element-4581" data-mp="24v6dywryghqg" />
            Reset
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={!dirty} data-id="element-4582" data-mp="1xbnme24nbg69">
            <Save className="w-3.5 h-3.5 mr-1" data-id="element-4583" data-mp="1sbj2010ma1ar" />
            Opslaan
          </Button>
          {saved && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded" data-id="element-4584" data-mp="1ngdh95iyaso9">
              Opgeslagen
            </span>}
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4" data-id="element-4585" data-mp="19n4g1s55pdgt">
        Geregistreerde, eindige uitzonderingen die de severity tijdelijk
        degraderen voor een scope (module / family / questionId). Elke entry
        heeft een eigenaar, een reden én een vervaldatum — permissiveness wordt
        zichtbare, aftelbare schuld in plaats van impliciet en oneindig.
      </p>

      {/* Summary stats */}
      {entries.length > 0 && <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-4586" data-mp="qo070jdm7jp5">
          <div className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-50" data-id="element-4587" data-mp="qqcwdhlpcwc8">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600" data-id="element-4588" data-mp="2elr8d03vtjx8">
              Totaal
            </p>
            <p className="text-lg font-bold text-slate-800 leading-tight" data-id="element-4589" data-mp="25k9wxv1f66ev">
              {entries.length}
            </p>
          </div>
          <div className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50" data-id="element-4590" data-mp="29l3hqfjkf53j">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700" data-id="element-4591" data-mp="1vtyfd5otbypi">
              Verloopt &lt; 7d
            </p>
            <p className="text-lg font-bold text-amber-800 leading-tight" data-id="element-4592" data-mp="20mrjbrcgj30j">
              {entries.filter(e => {
            const t = Date.parse(e.until);
            return Number.isFinite(t) && t - now < 7 * DAY_MS && t > now;
          }).length}
            </p>
          </div>
          <div className="px-3 py-2 rounded-lg border border-red-200 bg-red-50" data-id="element-4593" data-mp="1cxynduvu4v5x">
            <p className="text-[10px] font-bold uppercase tracking-wider text-red-700" data-id="element-4594" data-mp="1htw5a5mqy9kt">
              Verlopen
            </p>
            <p className="text-lg font-bold text-red-800 leading-tight" data-id="element-4595" data-mp="t9sqi7ib5dnp">
              {entries.filter(e => {
            const t = Date.parse(e.until);
            return Number.isFinite(t) && t < now;
          }).length}
            </p>
          </div>
        </div>}

      {/* Add button */}
      {!showForm && <Button variant="secondary" size="sm" onClick={() => {
      setDraft({
        ...EMPTY_DRAFT,
        until: defaultUntilIso()
      });
      setShowForm(true);
    }} className="mb-4" data-id="element-4596" data-mp="1h5oo1lzmsgoy">
          <Plus className="w-3.5 h-3.5 mr-1" data-id="element-4597" data-mp="1lyzwtso077ji" />
          Nieuwe grace toevoegen
        </Button>}

      {/* Add form */}
      {showForm && <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4 mb-4 space-y-3" data-id="element-4598" data-mp="1uzfz4yt0g8ms">
          <div className="flex items-center justify-between" data-id="element-4599" data-mp="1rbbaszxa9dsi">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-4600" data-mp="1rdo05y5deqfl">
              Nieuwe grace-entry
            </h3>
            <button type="button" onClick={() => {
          setShowForm(false);
          setFormError(null);
        }} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-4601" data-mp="1mj085pb5rdw9">
              Annuleer
            </button>
          </div>

          <p className="text-[11px] text-gray-600 leading-relaxed" data-id="element-4602" data-mp="25bck93rm5tjl">
            Scope: vul <strong data-id="element-4603" data-mp="2599ixnl0gdzk">één of meer</strong> in. Specificiteit:
            questionId &gt; family &gt; module.
          </p>

          <div className="grid grid-cols-3 gap-2" data-id="element-4604" data-mp="1vd6hkjxzgjdo">
            <div data-id="element-4605" data-mp="1vcn1z9vt272t">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4606" data-mp="1vf98r7avgijz">
                Module
              </label>
              <Input type="text" value={draft.module} onChange={e => setDraft({
            ...draft,
            module: e.target.value
          })} placeholder="bv. REK" data-id="element-4607" data-mp="jy9hmf09g9rj" />
            </div>
            <div data-id="element-4608" data-mp="35ihorxa1jc3b">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4609" data-mp="35hy78hoayppt">
                Family
              </label>
              <Input type="text" value={draft.family} onChange={e => setDraft({
            ...draft,
            family: e.target.value
          })} placeholder="bv. REK-CIJF-PLUS" data-id="element-4610" data-mp="3ac2ez35e71ry" />
            </div>
            <div data-id="element-4611" data-mp="3t9demouxrx7x">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4612" data-mp="3t7sjoy4pmc9j">
                Question ID
              </label>
              <Input type="text" value={draft.questionId} onChange={e => setDraft({
            ...draft,
            questionId: e.target.value
          })} placeholder="bv. REK-CIJF-PLUS-001" data-id="element-4613" data-mp="1ty3s5nvgydz7" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2" data-id="element-4614" data-mp="20u5ee7j8ry2f">
            <div data-id="element-4615" data-mp="1vtr4sl1zsg2a">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4616" data-mp="1vt7wlw25961g">
                Vervaldatum
              </label>
              <Input type="date" value={draft.until} onChange={e => setDraft({
            ...draft,
            until: e.target.value
          })} data-id="element-4617" data-mp="29rphlm0o04fo" />
            </div>
            <div data-id="element-4618" data-mp="2ekily8s81ar8">
              <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4619" data-mp="2em2301lpbpu2">
                Degradeer severity naar
              </label>
              <Input type="number" min={1} max={100} value={draft.degradeToSeverity} onChange={e => setDraft({
            ...draft,
            degradeToSeverity: e.target.value
          })} placeholder="50" data-id="element-4620" data-mp="2oiokcgetjyrc" />
            </div>
          </div>

          <div data-id="element-4621" data-mp="333squx2en11z">
            <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4622" data-mp="333ajb4aal0er">
              Eigenaar
            </label>
            <Input type="text" value={draft.owner} onChange={e => setDraft({
          ...draft,
          owner: e.target.value
        })} placeholder="bv. content-team / @jdoe" data-id="element-4623" data-mp="2oduwu0hsnh4v" />
          </div>

          <div data-id="element-4624" data-mp="25hd8lt25lzdd">
            <label className="block text-[11px] font-medium text-gray-700 mb-1" data-id="element-4625" data-mp="25gtoq6sy10wp">
              Reden
            </label>
            <textarea value={draft.reason} onChange={e => setDraft({
          ...draft,
          reason: e.target.value
        })} placeholder="Waarom is deze uitzondering nodig en wanneer kan hij dicht?" className="w-full min-h-[60px] rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" data-id="element-4626" data-mp="2aev7srqdd086" />
          </div>

          {formError && <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2" data-id="element-4627" data-mp="2u4fr6btylww1">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" data-id="element-4628" data-mp="2q0822djlaexa" />
              <p className="text-xs text-red-700" data-id="element-4629" data-mp="2l2ybuvodlz8e">{formError}</p>
            </div>}

          <div className="flex justify-end gap-2" data-id="element-4630" data-mp="2g4wj1j15eljq">
            <Button variant="ghost" size="sm" onClick={() => {
          setShowForm(false);
          setFormError(null);
        }} data-id="element-4631" data-mp="jatev5s0sbqt">
              Annuleer
            </Button>
            <Button variant="primary" size="sm" onClick={handleAdd} data-id="element-4632" data-mp="luydw51v86ny">
              <Plus className="w-3.5 h-3.5 mr-1" data-id="element-4633" data-mp="qtsur1g9vl5f" />
              Toevoegen aan ledger
            </Button>
          </div>
        </div>}

      {/* Entry list */}
      {entries.length === 0 && !showForm && <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-4 py-8 text-center" data-id="element-4634" data-mp="10j2oelqrlbvn">
          <ScrollText className="w-6 h-6 text-gray-400 mx-auto mb-2" data-id="element-4635" data-mp="2y6n7ajzup9v" />
          <p className="text-sm font-medium text-gray-700" data-id="element-4636" data-mp="7ux4g17jbqwv">
            Geen grace-entries actief
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-4637" data-mp="ctryjzayd1wa">
            Permissiveness is geen default — uitzonderingen moeten expliciet
            geregistreerd worden.
          </p>
        </div>}

      <div className="space-y-2" data-id="element-4638" data-mp="1o8x2t7qqf8i0">
        {sorted.map(({
        entry,
        idx
      }) => {
        const countdown = formatCountdown(entry.until, now);
        const spec = scopeSpecificity(entry.scope);
        return <div key={`${idx}-${entry.until}`} className={`rounded-lg border px-3 py-3 ${countdown.expired ? 'border-red-200 bg-red-50/40' : 'border-gray-100 bg-white'}`} data-id="element-4639" data-mp="3pamswidg40ed">
              <div className="flex items-start justify-between gap-3" data-id="element-4640" data-mp="26oz5f2tz5lx">
                <div className="min-w-0 flex-1 space-y-1.5" data-id="element-4641" data-mp="3uclx1uh0f1cd">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-4642" data-mp="36lgg1bzc230h">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${spec.cls}`} data-id="element-4643" data-mp="36jlwo23pmiyi">
                      <Tag className="w-2.5 h-2.5" data-id="element-4644" data-mp="3ffuuxdfybbj2" />
                      {spec.level}
                    </span>
                    <span className="font-mono text-xs text-gray-800 truncate" data-id="element-4645" data-mp="2mxphrrwzbz5q">
                      {scopeLabel(entry.scope)}
                    </span>
                    {typeof entry.degradeToSeverity === 'number' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200" data-id="element-4646" data-mp="28rx96k5bxgpi">
                        → sev {entry.degradeToSeverity}
                      </span>}
                  </div>
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4647" data-mp="23t3xmx9lrzrz">
                    {entry.reason}
                  </p>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500" data-id="element-4648" data-mp="2rif0qlefe69a">
                    <span className="inline-flex items-center gap-1" data-id="element-4649" data-mp="2whhyrlvznc7r">
                      <User className="w-3 h-3" data-id="element-4650" data-mp="2wff7yzn0hf1z" />
                      {entry.owner}
                    </span>
                    <span className={`inline-flex items-center gap-1 ${countdown.cls}`} data-id="element-4651" data-mp="2mjlkil7r815f">
                      <Clock className="w-3 h-3" data-id="element-4652" data-mp="1652f4t0g3fl1" />
                      {countdown.text}
                    </span>
                    <span className="text-gray-400" data-id="element-4653" data-mp="1b580xsk1a7v7">
                      ({new Date(entry.until).toLocaleDateString('nl-NL')})
                    </span>
                  </div>
                </div>
                <button type="button" onClick={() => handleRemove(idx)} className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50" title="Verwijder uit ledger" data-id="element-4654" data-mp="ne1ge51cont9">
                  <Trash2 className="w-4 h-4" data-id="element-4655" data-mp="1b3c68rh5wxsm" />
                </button>
              </div>
            </div>;
      })}
      </div>

      <p className="text-[11px] text-gray-500 mt-4 leading-relaxed" data-id="element-4656" data-mp="167dnpkiima04">
        💡 Tip: verlopen entries blijven zichtbaar als een ruststand-debt signal
        — ze gelden niet meer aan de pipeline-kant (de resolver{' '}
        <span className="font-mono" data-id="element-4657" data-mp="1tuv7asrmm1s0">findActiveGrace</span> filtert ze al), maar
        ze moeten alsnog opgeruimd of vernieuwd worden.
      </p>
    </div>;
}